import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { MODAL_SUBTITLE_OPTIONS_PREVIEW_LABEL } from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";

const DEFAULT_OPTIONS = {
  fontFamily: "Roboto",
  fontStyle: "bold",
  subtitleColor: "#FFFFFF",
  doHighlight: true,
  highlightColor: "#FFD700",
  highlightMode: "karaoke",
  hasBackground: false,
  backgroundColor: "#000000",
};

const getFontStyleFromOption = (fontStyle) => {
  switch (fontStyle) {
    case "bold":
      return { fontWeight: 700, fontStyle: "normal" };
    case "italic":
      return { fontWeight: 400, fontStyle: "italic" };
    case "bold_italic":
      return { fontWeight: 700, fontStyle: "italic" };
    default:
      return { fontWeight: 400, fontStyle: "normal" };
  }
};

const TEXT_STROKE =
  "1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 2px 0 0 #000, -2px 0 0 #000, 0 2px 0 #000, 0 -2px 0 #000";

const KARAOKE_CHAR_DELAY_MS = 150;
const WORD_DELAY_MS = 700;
const LOOP_PAUSE_MS = 1500;

const SubtitlePreview = ({
  options = {},
  sampleText = MODAL_SUBTITLE_OPTIONS_PREVIEW_LABEL,
}) => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const {
    fontFamily,
    fontStyle,
    subtitleColor,
    doHighlight,
    highlightColor,
    highlightMode,
    hasBackground,
    backgroundColor,
  } = opts;

  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  const { t } = useTranslationApp();
  const translatedSampleText = t(sampleText);
  const chars = translatedSampleText.split("");
  const words = translatedSampleText.split(/\s+/);

  useEffect(() => {
    if (!doHighlight || highlightMode !== "karaoke") return;

    const totalChars = chars.length;
    if (totalChars === 0) return;

    const interval = setInterval(() => {
      setCurrentCharIndex((prev) => {
        if (prev >= totalChars - 1) return -1;
        return prev + 1;
      });
    }, KARAOKE_CHAR_DELAY_MS);

    return () => clearInterval(interval);
  }, [doHighlight, highlightMode, translatedSampleText, chars]);

  useEffect(() => {
    if (!doHighlight || highlightMode !== "karaoke") return;
    if (currentCharIndex === -1) {
      const id = setTimeout(() => setCurrentCharIndex(0), LOOP_PAUSE_MS);
      return () => clearTimeout(id);
    }
  }, [currentCharIndex, doHighlight, highlightMode]);

  useEffect(() => {
    if (!doHighlight || highlightMode !== "word") return;

    const totalWords = words.length;
    if (totalWords === 0) return;

    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => {
        if (prev >= totalWords - 1) return -1;
        return prev + 1;
      });
    }, WORD_DELAY_MS);

    return () => clearInterval(interval);
  }, [doHighlight, highlightMode, translatedSampleText, words]);

  useEffect(() => {
    if (!doHighlight || highlightMode !== "word") return;
    if (currentWordIndex === -1) {
      const id = setTimeout(() => setCurrentWordIndex(0), LOOP_PAUSE_MS);
      return () => clearTimeout(id);
    }
  }, [currentWordIndex, doHighlight, highlightMode]);

  const { fontWeight, fontStyle: cssFontStyle } =
    getFontStyleFromOption(fontStyle);
  const baseTextStyle = {
    fontFamily: `"${fontFamily}", sans-serif`,
    fontSize: 18,
    fontWeight,
    fontStyle: cssFontStyle,
    textShadow: TEXT_STROKE,
    display: "inline",
  };

  const renderContent = () => {
    if (!doHighlight) {
      return (
        <Box
          component="span"
          sx={{
            ...baseTextStyle,
            color: subtitleColor,
          }}
        >
          {translatedSampleText}
        </Box>
      );
    }

    if (highlightMode === "karaoke") {
      const displayCharIndex =
        currentCharIndex === -1 ? chars.length - 1 : currentCharIndex;
      return (
        <Box component="span" sx={baseTextStyle}>
          {chars.map((char, i) => (
            <Box
              key={`${char}-${i}`}
              component="span"
              sx={{
                color: i <= displayCharIndex ? highlightColor : subtitleColor,
              }}
            >
              {char}
            </Box>
          ))}
        </Box>
      );
    }

    if (highlightMode === "word") {
      const displayWordIndex =
        currentWordIndex === -1 ? words.length - 1 : currentWordIndex;
      return (
        <Box component="span" sx={baseTextStyle}>
          {words.map((word, wordIdx) => {
            const isHighlighted = wordIdx === displayWordIndex;
            const isLast = wordIdx === words.length - 1;
            return (
              <Box key={`${word}-${wordIdx}`} component="span">
                <Box
                  component="span"
                  sx={{
                    color: isHighlighted ? highlightColor : subtitleColor,
                  }}
                >
                  {word}
                </Box>
                {!isLast && " "}
              </Box>
            );
          })}
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        backgroundColor: "#1a1a1a",
        borderRadius: 1,
        p: 2,
        minHeight: 80,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          ...(hasBackground && {
            backgroundColor: `${backgroundColor}cc`,
            px: 2,
            py: 1,
            borderRadius: 1,
          }),
          maxWidth: "100%",
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};

export default SubtitlePreview;
